import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { IconDevices } from "@/components/ui/icons";
import { useConnect } from "@/contexts/ConnectContext";
import { usePlayer } from "@/hooks/usePlayer";
import { sfx } from "@/lib/sfx";

const KIND_ICON = { desktop: "🖥️", mobile: "📱", tablet: "📱" } as const;

/** "Écouter sur…" — pick which of your open devices plays the music. */
export function DevicesButton({ light = false }: { light?: boolean }) {
  const connect = useConnect();
  const player = usePlayer();
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ right: number; bottom: number } | null>(null);

  useEffect(() => {
    if (!open) return;
    const on = (e: PointerEvent) => {
      const t = e.target as Node;
      if (boxRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("pointerdown", on);
    window.addEventListener("keydown", esc);
    return () => { document.removeEventListener("pointerdown", on); window.removeEventListener("keydown", esc); };
  }, [open]);

  const toggleOpen = () => {
    sfx.tap();
    // the playbar clips overflow, so the panel lives in a portal anchored
    // just above the button
    const r = boxRef.current?.getBoundingClientRect();
    if (r) setPos({ right: Math.max(8, window.innerWidth - r.right), bottom: window.innerHeight - r.top + 10 });
    setOpen((v) => !v);
  };

  if (!connect) return null;
  const { devices, selfId, selfName, activeRemote, playOn, rename, connected } = connect;
  const others = devices.filter((d) => d.id !== selfId);
  const playingHere = player.playing;
  const remoteOn = !!activeRemote;

  return (
    <div ref={boxRef} style={{ position: "relative" }}>
      <button title="Écouter sur un autre appareil" aria-label="Appareils"
              onClick={toggleOpen}
              className={remoteOn ? "devices-btn on" : "devices-btn"}
              style={{ color: light ? "#fff" : undefined }}>
        <IconDevices />
        {others.length > 0 && <span className="devices-dot" />}
      </button>
      {open && pos && createPortal(
        <div ref={panelRef} className="devices-panel" role="dialog" aria-label="Appareils"
             style={{ right: pos.right, bottom: pos.bottom }}>
          <div className="devices-title">Écouter sur</div>
          <DeviceRow icon={KIND_ICON.desktop} name={`${selfName} (cet appareil)`}
                     sub={playingHere ? `▶ ${player.current?.title ?? ""}` : remoteOn ? "Écouter ici" : "Prêt"}
                     active={playingHere}
                     onClick={() => { if (!playingHere) { sfx.pop(); playOn(selfId); setOpen(false); } }} />
          {others.map((d) => {
            const st = d.state;
            const isActive = activeRemote?.id === d.id;
            return (
              <DeviceRow key={d.id} icon={KIND_ICON[d.kind] ?? "🖥️"} name={d.name}
                         sub={st?.track ? `${st.playing ? "▶" : "⏸"} ${st.track.title} · ${st.track.artist?.name ?? ""}` : "Disponible"}
                         active={isActive}
                         onClick={() => { sfx.pop(); playOn(d.id); setOpen(false); }} />
            );
          })}
          {others.length === 0 && (
            <div className="devices-hint">
              {connected
                ? "Ouvre slowson sur ton téléphone ou un autre ordi (même compte) : il apparaîtra ici et tu pourras le contrôler d'ici."
                : "Connexion aux autres appareils…"}
            </div>
          )}
          <button className="devices-rename" onClick={() => {
            const n = window.prompt("Nom de cet appareil", selfName);
            if (n) rename(n);
          }}>Renommer cet appareil</button>
        </div>,
        document.body,
      )}
    </div>
  );
}

function DeviceRow({ icon, name, sub, active, onClick }: {
  icon: string; name: string; sub: string; active: boolean; onClick: () => void;
}) {
  return (
    <button className={"device-row" + (active ? " active" : "")} onClick={onClick}>
      <span className="device-icon">{icon}</span>
      <span style={{ minWidth: 0, textAlign: "left" }}>
        <span className="trunc" style={{ display: "block", fontWeight: 700 }}>{name}</span>
        <span className="trunc device-sub">{sub}</span>
      </span>
      {active && <span className="device-eq" aria-hidden="true"><i /><i /><i /></span>}
    </button>
  );
}
